import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  TrendingUp,
  Package,
  Users,
  DollarSign,
  ArrowDownRight,
  ArrowUpRight,
  RotateCcw,
  Tag,
  Truck,
  AlertOctagon,
  AlertTriangle,
  Award,
  ShoppingBag,
} from 'lucide-react';
import { adminReportsApi } from '../api/operations';
import { PageHeader } from '../components/Shell';
import { PageLoader, Skeleton } from '../components/ui';
import { money, number } from '../lib/format';

type ReportTab = 'sales' | 'products' | 'customers';

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('sales');
  const [salesPeriod, setSalesPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');

  // Queries
  const salesQuery = useQuery({
    queryKey: ['admin', 'reports', 'sales', salesPeriod],
    queryFn: () => adminReportsApi.sales(salesPeriod),
    enabled: activeTab === 'sales',
  });

  const productsQuery = useQuery({
    queryKey: ['admin', 'reports', 'products'],
    queryFn: () => adminReportsApi.products(),
    enabled: activeTab === 'products',
  });

  const customersQuery = useQuery({
    queryKey: ['admin', 'reports', 'customers'],
    queryFn: () => adminReportsApi.customers(),
    enabled: activeTab === 'customers',
  });

  return (
    <div>
      <PageHeader
        title="Raporlama ve Analitik"
        description="Satış kırılımları, ürün performansı ve müşteri sadakati"
      />

      {/* Ana Sekmeler */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-ink-200">
        <button
          type="button"
          onClick={() => setActiveTab('sales')}
          className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition ${
            activeTab === 'sales'
              ? 'border-brand-600 text-brand-700 font-semibold'
              : 'border-transparent text-ink-500 hover:text-ink-800'
          }`}
        >
          <TrendingUp className="h-4 w-4" />
          Satış Raporu
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition ${
            activeTab === 'products'
              ? 'border-brand-600 text-brand-700 font-semibold'
              : 'border-transparent text-ink-500 hover:text-ink-800'
          }`}
        >
          <Package className="h-4 w-4" />
          Ürün & Stok Raporu
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('customers')}
          className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition ${
            activeTab === 'customers'
              ? 'border-brand-600 text-brand-700 font-semibold'
              : 'border-transparent text-ink-500 hover:text-ink-800'
          }`}
        >
          <Users className="h-4 w-4" />
          Müşteri & Sadakat
        </button>
      </div>

      {/* 1. SATIŞ RAPORU */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* Periyot Filtresi */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-sm font-semibold text-ink-900">Satış Metrikleri & Finansal Kırılım</h2>
            <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
              {(
                [
                  { key: 'daily', label: 'Günlük' },
                  { key: 'weekly', label: 'Haftalık' },
                  { key: 'monthly', label: 'Aylık' },
                  { key: 'yearly', label: 'Yıllık' },
                ] as const
              ).map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setSalesPeriod(p.key)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                    salesPeriod === p.key
                      ? 'bg-ink-900 text-white shadow-sm'
                      : 'text-ink-600 hover:text-ink-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {salesQuery.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
          ) : salesQuery.data ? (
            <>
              {/* Finansal KPI Kartları */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Brüt Satış</span>
                    <DollarSign className="h-4 w-4 text-brand-600" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-ink-900">{money(salesQuery.data.grossSales)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">İndirim ve iade öncesi hacim</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Net Satış</span>
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-emerald-600">{money(salesQuery.data.netSales)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Brüt - İndirimler - İadeler</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>İade Tutarı</span>
                    <RotateCcw className="h-4 w-4 text-rose-500" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-rose-600">{money(salesQuery.data.refundAmount)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Müşterilere iade edilen tutar</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Uygulanan İndirimler</span>
                    <Tag className="h-4 w-4 text-amber-500" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-amber-600">{money(salesQuery.data.discountAmount)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Kupon ve kampanya indirimleri</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Kargo Geliri</span>
                    <Truck className="h-4 w-4 text-indigo-500" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-ink-900">{money(salesQuery.data.shippingRevenue)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Kargo tahsilat toplamı</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Hesaplanan KDV</span>
                    <PercentIcon className="h-4 w-4 text-sky-500" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-ink-900">{money(salesQuery.data.taxAmount)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Toplam tahakkuk eden vergi</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Toplam Sipariş</span>
                    <ShoppingBag className="h-4 w-4 text-purple-500" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-ink-900">{number(salesQuery.data.orderCount)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Tamamlanan sipariş adedi</p>
                </div>

                <div className="card p-4">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Ortalama Sepet Tutarı (AOV)</span>
                    <ArrowUpRight className="h-4 w-4 text-teal-600" />
                  </div>
                  <p className="mt-2 text-xl font-bold text-teal-700">{money(salesQuery.data.averageOrderValue)}</p>
                  <p className="mt-1 text-[11px] text-ink-400">Sipariş başına ortalama ciro</p>
                </div>
              </div>

              {/* Satış Trend Grafiği */}
              <section className="card p-5">
                <h3 className="mb-4 text-sm font-semibold text-ink-900">Gelir ve Net Satış Trendi</h3>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesQuery.data.breakdown} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1d67f1" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#1d67f1" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(val) => `₺${val}`} />
                      <Tooltip
                        formatter={(val: number, name: string) => [
                          money(val),
                          name === 'grossSales' ? 'Brüt Satış' : 'Net Satış',
                        ]}
                      />
                      <Area
                        type="monotone"
                        dataKey="grossSales"
                        stroke="#1d67f1"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#grossGrad)"
                        name="grossSales"
                      />
                      <Area
                        type="monotone"
                        dataKey="netSales"
                        stroke="#10b981"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#netGrad)"
                        name="netSales"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </section>

              {/* Satış Kırılım Tablosu */}
              <section className="card overflow-hidden">
                <h3 className="border-b border-ink-200 px-5 py-3.5 text-sm font-semibold text-ink-900">
                  Dönemsel Satış Tablosu ({salesPeriod})
                </h3>
                <div className="scroll-x">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-ink-200 bg-ink-50/70 text-ink-500 uppercase tracking-wider font-semibold">
                        <th className="px-4 py-3">Dönem</th>
                        <th className="px-4 py-3 text-right">Brüt Satış</th>
                        <th className="px-4 py-3 text-right">İndirim</th>
                        <th className="px-4 py-3 text-right">İade</th>
                        <th className="px-4 py-3 text-right">Kargo Geliri</th>
                        <th className="px-4 py-3 text-right font-bold text-ink-900">Net Satış</th>
                        <th className="px-4 py-3 text-center">Sipariş Sayısı</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {salesQuery.data.breakdown.map((row, idx) => (
                        <tr key={idx} className="hover:bg-ink-50/50">
                          <td className="px-4 py-2.5 font-medium text-ink-900">{row.periodLabel}</td>
                          <td className="px-4 py-2.5 text-right text-ink-700">{money(row.grossSales)}</td>
                          <td className="px-4 py-2.5 text-right text-amber-600">-{money(row.discountAmount)}</td>
                          <td className="px-4 py-2.5 text-right text-rose-600">-{money(row.refundAmount)}</td>
                          <td className="px-4 py-2.5 text-right text-ink-600">{money(row.shippingRevenue)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-emerald-600">{money(row.netSales)}</td>
                          <td className="px-4 py-2.5 text-center font-mono text-ink-700">{row.orderCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : null}
        </div>
      )}

      {/* 2. ÜRÜN & STOK RAPORU */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          {productsQuery.isLoading ? (
            <PageLoader />
          ) : productsQuery.data ? (
            <div className="space-y-6">
              {/* En Çok Satanlar & En Az Satanlar */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* En Çok Satanlar */}
                <section className="card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <Award className="h-4 w-4 text-amber-500" />
                      En Çok Satan Ürünler (Top 10)
                    </h3>
                  </div>
                  <div className="divide-y divide-ink-100">
                    {productsQuery.data.topSellingProducts.length === 0 ? (
                      <p className="py-8 text-center text-xs text-ink-400">Henüz satış verisi bulunmuyor.</p>
                    ) : (
                      productsQuery.data.topSellingProducts.map((p, idx) => (
                        <div key={p.productId} className="flex items-center gap-3 py-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-bold text-ink-700">
                            {idx + 1}
                          </span>
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt="" className="h-9 w-9 rounded-lg object-cover border border-ink-200" />
                          ) : (
                            <div className="h-9 w-9 rounded-lg bg-ink-100 flex items-center justify-center text-ink-400">
                              <Package className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-ink-900">{p.title}</p>
                            <p className="text-[11px] text-ink-400 font-mono">{p.sku || 'SKU yok'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-bold text-emerald-600">{p.unitsSold} adet</p>
                            <p className="text-[11px] text-ink-500">{money(p.totalRevenue)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                {/* En Az Satanlar */}
                <section className="card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <ArrowDownRight className="h-4 w-4 text-ink-500" />
                      En Az Satan Ürünler
                    </h3>
                  </div>
                  <div className="divide-y divide-ink-100">
                    {productsQuery.data.leastSellingProducts.length === 0 ? (
                      <p className="py-8 text-center text-xs text-ink-400">Veri bulunamadı.</p>
                    ) : (
                      productsQuery.data.leastSellingProducts.map((p, idx) => (
                        <div key={p.productId} className="flex items-center gap-3 py-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-medium text-ink-600">
                            {idx + 1}
                          </span>
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt="" className="h-9 w-9 rounded-lg object-cover border border-ink-200" />
                          ) : (
                            <div className="h-9 w-9 rounded-lg bg-ink-100 flex items-center justify-center text-ink-400">
                              <Package className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-ink-800">{p.title}</p>
                            <p className="text-[11px] text-ink-400 font-mono">{p.sku || 'SKU yok'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-ink-700">{p.unitsSold} adet</p>
                            <p className="text-[11px] text-ink-400">{money(p.totalRevenue)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>

              {/* Stok Durumları: Stokta Olmayanlar & Düşük Stoktakiler */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Stokta Olmayanlar */}
                <section className="card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-rose-700">
                      <AlertOctagon className="h-4 w-4 text-rose-600" />
                      Stokta Olmayan Ürünler ({productsQuery.data.outOfStockProducts.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-ink-100 max-h-80 overflow-y-auto pr-1">
                    {productsQuery.data.outOfStockProducts.length === 0 ? (
                      <p className="py-8 text-center text-xs text-emerald-600">Harika! Stokta biten ürün yok.</p>
                    ) : (
                      productsQuery.data.outOfStockProducts.map((p) => (
                        <div key={p.variantId} className="flex items-center justify-between py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-ink-900">{p.title}</p>
                            <p className="text-[11px] text-ink-400">
                              {p.variantTitle ? `${p.variantTitle} · ` : ''}
                              <span className="font-mono">{p.sku}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800">
                              0 Stok
                            </span>
                            <p className="mt-0.5 text-[11px] text-ink-500">{money(p.price)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                {/* Düşük Stoktakiler */}
                <section className="card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Düşük Stoklu Ürünler ({productsQuery.data.lowStockProducts.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-ink-100 max-h-80 overflow-y-auto pr-1">
                    {productsQuery.data.lowStockProducts.length === 0 ? (
                      <p className="py-8 text-center text-xs text-ink-400">Kritik stok uyarısı yok.</p>
                    ) : (
                      productsQuery.data.lowStockProducts.map((p) => (
                        <div key={p.variantId} className="flex items-center justify-between py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-ink-900">{p.title}</p>
                            <p className="text-[11px] text-ink-400">
                              {p.variantTitle ? `${p.variantTitle} · ` : ''}
                              <span className="font-mono">{p.sku}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                              Kalan: {p.stock} adet
                            </span>
                            <p className="mt-0.5 text-[11px] text-ink-400">Eşik: {p.lowStockThreshold ?? 5}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>

              {/* En Karlı Ürünler (Profit Margin) */}
              <section className="card overflow-hidden">
                <div className="border-b border-ink-200 px-5 py-4">
                  <h3 className="text-sm font-semibold text-ink-900">En Karlı Ürünler (Kar Marjı Analizi)</h3>
                  <p className="text-xs text-ink-500 mt-0.5">
                    Güncel birim kâr marjı ile geçmiş siparişlerde kasaya giren gerçek satış fiyatlarına göre gerçekleşen net kâr.
                  </p>
                </div>

                <div className="scroll-x">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-ink-200 bg-ink-50/70 text-ink-500 uppercase tracking-wider font-semibold">
                        <th className="px-4 py-3">Ürün</th>
                        <th className="px-4 py-3 font-mono">SKU</th>
                        <th className="px-4 py-3 text-right">Alış Maliyeti</th>
                        <th className="px-4 py-3 text-right">Güncel Satış Fiyatı</th>
                        <th className="px-4 py-3 text-right text-emerald-600">Güncel Birim Kâr</th>
                        <th className="px-4 py-3 text-right font-bold text-brand-700">Kâr Marjı %</th>
                        <th className="px-4 py-3 text-center">Satılan Adet</th>
                        <th className="px-4 py-3 text-right">Kasaya Giren (Ciro)</th>
                        <th className="px-4 py-3 text-right font-bold text-ink-900">Gerçekleşen Kâr</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {productsQuery.data.mostProfitableProducts.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-xs text-ink-400">
                            Maliyeti tanımlanmış karlı ürün bulunamadı. Ürün düzenleme ekranından alış maliyeti girebilirsiniz.
                          </td>
                        </tr>
                      ) : (
                        productsQuery.data.mostProfitableProducts.map((p) => {
                          const hasHistoricalPriceDiff =
                            p.unitsSold > 0 &&
                            p.averageSalePrice !== undefined &&
                            Math.abs(p.averageSalePrice - p.salePrice) >= 0.01;

                          return (
                            <tr key={p.productId} className="hover:bg-ink-50/50">
                              <td className="px-4 py-3 font-medium text-ink-900">{p.title}</td>
                              <td className="px-4 py-3 font-mono text-ink-500">{p.sku}</td>
                              <td className="px-4 py-3 text-right text-ink-600">{money(p.costPrice)}</td>
                              <td className="px-4 py-3 text-right text-ink-900 font-medium">
                                <div>{money(p.salePrice)}</div>
                                {hasHistoricalPriceDiff ? (
                                  <div className="text-[11px] font-normal text-ink-400">
                                    Satılan ort: {money(p.averageSalePrice!)}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">
                                {p.profitAmount >= 0 ? `+${money(p.profitAmount)}` : money(p.profitAmount)}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-brand-700">%{p.profitMarginPercentage}</td>
                              <td className="px-4 py-3 text-center font-mono text-ink-700">{p.unitsSold}</td>
                              <td className="px-4 py-3 text-right text-ink-700 font-medium">
                                {money(p.totalRevenue ?? 0)}
                              </td>
                              <td
                                className={`px-4 py-3 text-right font-bold ${
                                  p.totalProfit > 0
                                    ? 'text-emerald-600'
                                    : p.totalProfit < 0
                                      ? 'text-red-600'
                                      : 'text-ink-900'
                                }`}
                              >
                                {p.totalProfit > 0 ? `+${money(p.totalProfit)}` : money(p.totalProfit)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          ) : null}
        </div>
      )}

      {/* 3. MÜŞTERİ & SADAKAT RAPORU */}
      {activeTab === 'customers' && (
        <div className="space-y-6">
          {customersQuery.isLoading ? (
            <PageLoader />
          ) : customersQuery.data ? (
            <>
              {/* Müşteri KPI Kartları */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="card p-5">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Yeni Müşteriler</span>
                    <Users className="h-4 w-4 text-brand-600" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-ink-900">
                    {number(customersQuery.data.newCustomersCount)}
                  </p>
                  <p className="mt-1 text-xs text-ink-400">Son 12 ayda kaydolanlar</p>
                </div>

                <div className="card p-5">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Aktif Müşteriler</span>
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-emerald-600">
                    {number(customersQuery.data.activeCustomersCount)}
                  </p>
                  <p className="mt-1 text-xs text-ink-400">Dönemde sipariş veren tekil müşteri</p>
                </div>

                <div className="card p-5">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Tekrar Eden Müşteri Oranı (Retention)</span>
                    <RotateCcw className="h-4 w-4 text-indigo-600" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-indigo-700">
                    %{customersQuery.data.returningCustomerRate}
                  </p>
                  <p className="mt-1 text-xs text-ink-400">2 veya daha fazla sipariş verenler</p>
                </div>

                <div className="card p-5">
                  <div className="flex items-center justify-between text-xs text-ink-500">
                    <span>Müşteri Yaşam Boyu Değeri (CLV)</span>
                    <Award className="h-4 w-4 text-amber-500" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-amber-700">
                    {money(customersQuery.data.customerLifetimeValue)}
                  </p>
                  <p className="mt-1 text-xs text-ink-400">Müşteri başına ortalama toplam ciro</p>
                </div>
              </div>

              {/* Müşteri Artış Trendi Grafiği */}
              <section className="card p-5">
                <h3 className="mb-4 text-sm font-semibold text-ink-900">Müşteri Kazanım ve Aktivite Trendi</h3>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={customersQuery.data.newCustomersTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip />
                      <Bar dataKey="newCustomerCount" name="Yeni Kayıtlar" fill="#1d67f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="activeCustomerCount" name="Aktif Sipariş Verenler" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            </>
          ) : null}
        </div>
      )}

      {/* 4. DÖNÜŞÜM HUNİSİ (CONVERSION FUNNEL) */}
    </div>
  );
}

function PercentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" x2="5" y1="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
